import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Tag, Select, Space, Button, Typography, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { adminAPI } from '../api/admin';

const { Text } = Typography;

const TEST_TYPE_LABELS = {
  platform_9_3_4: { label: 'سکو ۹ و ۳/۴', color: 'blue' },
  wand: { label: 'چوبدستی', color: 'purple' },
  sorting_hat: { label: 'کلاه گروه‌بندی', color: 'gold' },
  patronus: { label: 'پاترونوس', color: 'green' }
};

export default function TestResultManager() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ testType: undefined, passed: undefined });

  const fetchList = useCallback(async (targetPage = page) => {
    setLoading(true);
    try {
      const params = { page: targetPage, limit };
      if (filters.testType) params.testType = filters.testType;
      if (filters.passed !== undefined) params.passed = filters.passed;
      const { data } = await adminAPI.getTestResults(params);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      message.error('خطا در دریافت نتایج آزمون‌ها');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, filters]);

  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters]);

  const columns = [
    {
      title: 'زمان',
      dataIndex: 'createdAt',
      width: 160,
      render: (v) => moment(v).format('YYYY-MM-DD HH:mm')
    },
    {
      title: 'کاربر',
      dataIndex: 'user',
      render: (u) => u ? (
        <Text>{u.farsiFirstName || u.firstName} {u.farsiLastName || u.lastName}</Text>
      ) : <Text type="secondary">—</Text>
    },
    {
      title: 'نوع آزمون',
      dataIndex: 'testType',
      width: 160,
      render: (t) => {
        const meta = TEST_TYPE_LABELS[t] || { label: t, color: 'default' };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      }
    },
    {
      title: 'نمره',
      dataIndex: 'score',
      width: 90,
      render: (s) => s ?? '—'
    },
    {
      title: 'وضعیت',
      dataIndex: 'passed',
      width: 110,
      render: (p) => p ? <Tag color="success">قبول</Tag> : <Tag color="error">رد</Tag>
    },
    {
      title: 'نتیجه (خام)',
      dataIndex: 'result',
      render: (r) => (
        <pre style={{ margin: 0, fontSize: 11, maxWidth: 320, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {typeof r === 'object' ? JSON.stringify(r) : String(r)}
        </pre>
      )
    }
  ];

  return (
    <div>
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title="فیلترها"
        extra={<Button icon={<ReloadOutlined />} onClick={() => fetchList(page)}>بروزرسانی</Button>}
      >
        <Space wrap>
          <Select
            allowClear
            placeholder="نوع آزمون"
            style={{ width: 180 }}
            value={filters.testType}
            onChange={(v) => { setPage(1); setFilters((f) => ({ ...f, testType: v })); }}
            options={Object.entries(TEST_TYPE_LABELS).map(([value, meta]) => ({ value, label: meta.label }))}
          />
          <Select
            allowClear
            placeholder="وضعیت"
            style={{ width: 140 }}
            value={filters.passed}
            onChange={(v) => { setPage(1); setFilters((f) => ({ ...f, passed: v })); }}
            options={[
              { value: 'true', label: 'فقط قبول' },
              { value: 'false', label: 'فقط رد' }
            ]}
          />
        </Space>
      </Card>

      <Card size="small">
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: false,
            onChange: (p) => setPage(p)
          }}
        />
      </Card>
    </div>
  );
}
